export type RoadmapStatus = 'planned' | 'in-progress' | 'shipped';

export interface RoadmapItem {
  title: string;
  description: string;
  status: RoadmapStatus;
  category: string;
  /** ISO date (YYYY-MM-DD): the real ship date. Shipped items only; planned and in-progress items carry no date. */
  date?: string;
  /** Optional display override when the exact day is not recorded (shown instead of the formatted date). */
  dateLabel?: string;
  /** Planned items only: priority rank (1 = first to ship). */
  rank?: number;
  /** Planned items only: one line on why it sits where it does. */
  why?: string;
}

/**
 * MAINTENANCE RULES (owner: SEO agent, see seo-agent/DECISIONS.md):
 * - Planned and in-progress items have NO dates and no cadence promise (founder decision, 2026-10-26). Order = priority and can change.
 * - When a feature ships: set status 'shipped', date = real ship date, add a changelog entry, move it out of the planned list.
 * - The next planned item becomes 'in-progress' when work starts.
 * - Always bump ROADMAP_UPDATED when anything on this page changes.
 */
export const ROADMAP_UPDATED = '2026-10-28';
/** @deprecated No cadence is promised any more; kept only so older imports keep compiling. */
export const ROADMAP_CADENCE_DAYS = 0;

export const ROADMAP_ITEMS: RoadmapItem[] = [
  // The pipeline in priority order (no dates; priorities can change)
  { rank: 1, status: 'planned', category: 'Channels', title: 'Email-to-ticket',
    description: 'Forward your support inbox and every email becomes a ticket automatically.',
    why: 'Email is the first thing buyers check for in helpdesk checklists, and every other channel builds on a single ticket queue.' },
  { rank: 2, status: 'planned', category: 'Channels', title: 'Slack integration',
    description: 'Raise and update tickets without leaving Slack.',
    why: 'Internal and IT teams take requests in chat; this puts the queue where the requests already are.' },
  { rank: 3, status: 'planned', category: 'Workflow', title: 'CSAT surveys',
    description: 'Ask customers how it went after every ticket closes.',
    why: 'Shows whether support is working. It is a standard item on helpdesk buying lists and stays free in Kora.' },
  { rank: 4, status: 'planned', category: 'Developers', title: 'MCP server for all LLMs',
    description: 'Let ChatGPT, Claude and other AI assistants create, read and update your Kora tickets.',
    why: 'Small to build on the existing API and lets AI assistants work with your tickets.' },
  { rank: 5, status: 'planned', category: 'Portal', title: 'Portal custom domain and SSO',
    description: 'Serve the customer portal on your own domain and let customers sign in with single sign-on.',
    why: 'Matters most for larger customers, so it comes after the features every team needs.' },
  { rank: 6, status: 'planned', category: 'Channels', title: 'Live chat',
    description: 'Chat widget for your website that lands as tickets.',
    why: 'The largest build, and it works best once email and Slack share the same queue.' },

  // Shipped (newest first)
  { date: '2026-10-27', status: 'shipped', category: 'AI', title: 'Kora AI: reply drafting',
    description: 'A Draft reply button in the ticket composer writes a reply from your exceptions, knowledge base and published help articles, with sources shown. Your team edits and sends. 1 credit per draft; no draft, no charge.' },
  { date: '2026-10-27', status: 'shipped', category: 'AI', title: 'Kora AI: resolution agent',
    description: 'Answers customer-portal and dashboard tickets from your own material, with citations. Choose Auto or Approval required; risky topics always go to a person. 3 credits per confirmed resolution.' },
  { date: '2026-10-19', status: 'shipped', category: 'Product', title: 'New dashboard design',
    description: 'A redesigned dashboard in Kora\'s warm design language.' },
  { date: '2026-10-18', status: 'shipped', category: 'Portal', title: 'Customer portal',
    description: 'Customers sign in with an emailed link and see only their own requests.' },
  { date: '2026-10-16', dateLabel: 'Oct 2026', status: 'shipped', category: 'Help center', title: 'Magic Helpdesk',
    description: 'Reads your website and drafts 4–15 help articles for you to review and publish.' },
  { date: '2026-10-05', status: 'shipped', category: 'Help center', title: 'Help center',
    description: 'Branded knowledge base with search, article analytics and "Was this helpful?" votes.' },
  { date: '2026-10-03', status: 'shipped', category: 'Workflow', title: 'Automation rules',
    description: 'If-this-then-that rules with a dry run so you can test them safely before enabling.' },
  { date: '2026-10-01', status: 'shipped', category: 'Workflow', title: 'SLA tracking',
    description: 'Response and resolution targets with business-hours clocks that pause while waiting on a customer.' },
  { date: '2026-09-27', status: 'shipped', category: 'Billing', title: 'Pay-as-you-go credits',
    description: '$0.10 per ticket created, unlimited members, no monthly plans.' },
  { date: '2026-08-22', status: 'shipped', category: 'Developers', title: 'REST API (20+ endpoints)',
    description: 'Full API for tickets, comments, members and workspace management.' },
  { date: '2026-07-08', status: 'shipped', category: 'Developers', title: 'Webhooks (10 events)',
    description: 'HMAC-SHA256 signed payloads for ticket and help-center events.' },
  { date: '2026-07-01', status: 'shipped', category: 'Developers', title: 'Embedded sessions',
    description: 'Show Kora inside your own product with a scoped session token.' },
  { date: '2026-06-15', status: 'shipped', category: 'Team', title: 'Team analytics',
    description: 'Live counts of created, open and resolved tickets, resolution time and category trends.' },
  { date: '2026-05-20', status: 'shipped', category: 'AI', title: 'AI Analyst',
    description: 'Ask plain-English questions about your tickets and get a safe read-only SQL answer scoped to your workspace.' },
  { date: '2026-05-15', status: 'shipped', category: 'Team', title: 'Roles (6)',
    description: 'Owner, Admin, Manager, Operations, Agent and Viewer, checked server-side on every request.' },
  { date: '2026-05-01', dateLabel: 'Live before Oct 2026', status: 'shipped', category: 'AI', title: 'AI ticket structuring',
    description: 'Paste any raw text and Kora\'s AI writes a clean title and sets the category and priority.' },
];

export function formatRoadmapDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
